"use client";

// CardImage (not next/image): these 12 zodiac icons sit well below the fold on
// mobile, but as plain next/image they carried a real `src` in the SSR HTML, so
// the browser's native lazy heuristic began fetching them during the LCP window
// and they competed with the hero for Slow-4G bandwidth. They were the entire
// gap between home (22 competing images, LCP 4.8s) and section (10, LCP 3.0s).
import Image from "@/components/ui/CardImage";
import React from "react";

export type ZodiacSignName =
  | "mesham" // மேஷம்
  | "rishabam" // ரிஷபம்
  | "mithunam" // மிதுனம்
  | "kadagam" // கடகம்
  | "simmam" // சிம்மம்
  | "kanni" // கன்னி
  | "thulam" // துலாம்
  | "viruchigam" // விருச்சிகம்
  | "dhanusu" // தனுசு
  | "magaram" // மகரம்
  | "kumbam" // கும்பம்
  | "meenam"; // மீனம்

export type ZodiacSignData = {
  id: ZodiacSignName;
  signName: string;
  dateRange: string;
  image: string;
  tagline: string;
};

export const zodiacSignsData: Record<ZodiacSignName, ZodiacSignData> = {
  mesham: {
    id: "mesham",
    signName: "மேஷம்",
    dateRange: "March 21 - April 19",
    image: "https://media.dinamani.com/dinamani/2026-02-27/a2m82fgm/மேஷம்.png",
    tagline: "போட்டி",
  },
  rishabam: {
    id: "rishabam",
    signName: "ரிஷபம்",
    dateRange: "April 20 - May 20",
    image: "https://media.dinamani.com/dinamani/2026-02-27/83edr9kt/ரிஷபம்.png",
    tagline: "முன்னேற்றி",
  },
  mithunam: {
    id: "mithunam",
    signName: "மிதுனம்",
    dateRange: "May 21 - June 20",
    image: "https://media.dinamani.com/dinamani/2026-02-27/lj0g89nb/மிதுனம்.png",
    tagline: "நலம்",
  },
  kadagam: {
    id: "kadagam",
    signName: "கடகம்",
    dateRange: "June 21 - July 22",
    image: "https://media.dinamani.com/dinamani/2026-02-27/bf98g0id/கடகம்.png",
    tagline: "நலம்",
  },
  simmam: {
    id: "simmam",
    signName: "சிம்மம்",
    dateRange: "July 23 - August 22",
    image: "https://media.dinamani.com/dinamani/2026-02-27/k578m1wa/சிம்மம்.png",
    tagline: "இரக்கம்",
  },
  kanni: {
    id: "kanni",
    signName: "கன்னி",
    dateRange: "August 23 - September 22",
    image: "https://media.dinamani.com/dinamani/2026-02-27/nqlugwra/கன்னி.png",
    tagline: "சிந்தனை",
  },
  thulam: {
    id: "thulam",
    signName: "துலாம்",
    dateRange: "September 23 - October 22",
    image: "https://media.dinamani.com/dinamani/2026-02-27/kf5gg962/துலாம்.png",
    tagline: "மறதி",
  },
  viruchigam: {
    id: "viruchigam",
    signName: "விருச்சிகம்",
    dateRange: "October 23 - November 21",
    image: "https://media.dinamani.com/dinamani/2026-02-27/tvnpflbt/விருச்சிகம்.png",
    tagline: "சாதனை",
  },
  dhanusu: {
    id: "dhanusu",
    signName: "தனுசு",
    dateRange: "November 22 - December 21",
    image: "https://media.dinamani.com/dinamani/2026-02-27/ot2blw1o/தனுசு.png",
    tagline: "நட்பு",
  },
  magaram: {
    id: "magaram",
    signName: "மகரம்",
    dateRange: "December 22 - January 19",
    image: "https://media.dinamani.com/dinamani/2026-02-27/6x1o4jik/மகரம்.png",
    tagline: "வெற்றி",
  },
  kumbam: {
    id: "kumbam",
    signName: "கும்பம்",
    dateRange: "January 20 - February 18",
    image: "https://media.dinamani.com/dinamani/2026-02-27/l5aydt3r/கும்பம்.png",
    tagline: "அசதி",
  },
  meenam: {
    id: "meenam",
    signName: "மீனம்",
    dateRange: "February 19 - March 20",
    image: "https://media.dinamani.com/dinamani/2026-02-27/j2826xsl/மீனம்.png",
    tagline: "புகழ்",
  },
};

type ZodiacSignProps = {
  sign: ZodiacSignName;
  topText?: string;
  bottomText?: string;
  size?: number;
  className?: string;
  textClassName?: string;
  style?: React.CSSProperties;
};

export default function ZodiacSign({
  sign,
  topText,
  bottomText,
  size = 56,
  className = "",
  textClassName = "text-center text-sm mt-1",
  style,
}: ZodiacSignProps) {
  const signData = zodiacSignsData[sign];

  if (!signData) {
    console.error(`Zodiac sign '${sign}' not found`);
    return null;
  }

  return (
    <div className={`flex flex-col items-center ${className}`} style={style}>
      {topText && <div className={`${textClassName} mb-1`}>{topText}</div>}
      <div
        className="relative max-w-[56px] sm:max-w-[56px] md:max-w-[56px] lg:max-w-[56px] mb-2 sm:mb-3"
        style={{ width: size, height: size }}
      >
        <Image
          src={signData.image}
          alt={sign}
          width={size}
          height={size}
          className="object-contain w-full h-full"
          unoptimized={process.env.NODE_ENV !== "production"}
        />
      </div>
      {bottomText && (
        <div className={`${textClassName} mt-1 text-16-tamil-900 text-1B1B1B`}>
          {bottomText}
        </div>
      )}
    </div>
  );
}